import { Grid } from '@mui/material';
import { Stack } from 'components';
import dayjs from 'dayjs';
import { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';
import {
  howDidYouReachThemData,
  typesOfSupportData,
  whatTypeOfPlaceWereYouInData,
  whoDidYouReachData,
  whoWereYouTryingToReachData
} from 'store/clients/clientsSlice';
import { useAppSelector } from 'store/hooks';

import { getNoteTypeData } from '../noteEdit/fields/noteType/noteType';
import style from './noteView.module.scss';

type NoteItem = ClientProfileContentItem | Omit<ClientProfileContentItem, 'noteHistory'>;

type DisplayProps = {
  note: NoteItem;
};

export const TimeSpent = ({ note }: DisplayProps) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Time Spent:</p>
      <p className={style.text}>{note.timeSpentMin} Mins</p>
    </Stack>
  );
};

export const NextContact = ({ note }: DisplayProps) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Next Contact:</p>
      <p className={style.text}>
        {note.nextContact && dayjs(note.nextContact).isValid()
          ? dayjs(note?.nextContact).format('MM/DD/YYYY')
          : ''}
      </p>
    </Stack>
  );
};

export const TypeOfSupport = ({ note }: DisplayProps) => {
  const sortValues = (val: string[] | string, sortOrder: any[]) => {
    if (Array.isArray(val)) {
      return sortOrder
        .filter((item) => val.includes(item.id.toString()))
        .map((item) => item.name)
        .join(', ');
    } else {
      return val;
    }
  };

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>What type of support did you provide?</p>
      <p className={style.text}>{sortValues(note.typeOfSupport, typesOfSupportData)}</p>
    </Stack>
  );
};

export const Sites = ({ note }: DisplayProps) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>What specific site?</p>
      <p className={style.text}>{note.siteName}</p>
    </Stack>
  );
};

export const NoteType = ({ note }: DisplayProps) => {
  const noteTypeData = getNoteTypeData({ canUpdateManagerReview: true });
  const noteType = noteTypeData.find((n) => n.id === note.noteType);

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Note Type:</p>
      <p className={style.text}>{noteType?.name}</p>
    </Stack>
  );
};

export const StartTime = ({ note }: DisplayProps) => {
  const timeFormatedValue =
    dayjs(note?.startTimeOfEncounter, ['hh:mm A', 'HH:mm:ss'], true).format('h:mm A') !==
    'Invalid Date'
      ? dayjs(note?.startTimeOfEncounter, ['hh:mm A', 'HH:mm:ss'], true).format('h:mm A')
      : '';

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Start Time:</p>
      <p className={style.text}>{timeFormatedValue}</p>
    </Stack>
  );
};

export const DateOfEncounter = ({ note }: DisplayProps) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Date of Encounter:</p>
      <p className={style.text}>{dayjs(note?.dateOfEncounter).format('MM/DD/YYYY')}</p>
    </Stack>
  );
};

export const ZCodes = ({ note }: DisplayProps) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>SDOH Z codes:</p>
      <Stack display="flex" flexDirection="column" gap="12px">
        {note.selectedZCodes?.map((selectedZCode) => {
          return (
            <Stack key={selectedZCode.id} display="flex" flexDirection="column" gap="4px">
              <p className={style.text}>
                {selectedZCode.code} - {selectedZCode.description}
              </p>
              <div>
                {selectedZCode.selectedExplanations.map((e) => {
                  return (
                    <p key={e.id} className={style.text}>
                      {e.explanation}
                    </p>
                  );
                })}
              </div>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

export const Manager = ({ note }: DisplayProps) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Manager:</p>
      <p className={style.text}>{note.managerName}</p>
    </Stack>
  );
};

export const Chw = ({ note }: DisplayProps) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>CHW:</p>
      <p className={style.text}>{note.chwName}</p>
    </Stack>
  );
};

export const WhichProvider = ({ note }: DisplayProps) => {
  const providers = useAppSelector((state) => state.clients.billingProviders.data);
  const provider = providers.find(
    (p) => p.id.toString() === note.billingProviderId?.toString()
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Which provider?</p>
      <p className={style.text}>{provider}</p>
    </Stack>
  );
};

export const ManagerReviewProvider = ({ note }: DisplayProps) => {
  const providers = useAppSelector((state) => state.clients.billingProviders.data);
  const provider = providers.find(
    (p) => p.id.toString() === note.billingProviderId?.toString()
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Provider:</p>
      <p className={style.text}>{provider}</p>
    </Stack>
  );
};

export const OtherProviderName = ({ note }: DisplayProps) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Provider Name:</p>
      <p className={style.text}>{note.otherProviderName}</p>
    </Stack>
  );
};

export const DocumentationTime = ({ note }: DisplayProps) => {
  const getDocumentationTime = (documentationTime: number | null) => {
    if (documentationTime == null || documentationTime === 0) {
      return '';
    }
    return `${documentationTime} ${documentationTime > 1 ? 'mins' : 'min'}`;
  };

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Documentation Time:</p>
      <p className={style.text}>{getDocumentationTime(note.documentationTime)}</p>
    </Stack>
  );
};

export const WhoDidYouReach = ({ note }: DisplayProps) => {
  const whoDidYouReachNames = note.whoDidYouReach
    .map((w) => whoDidYouReachData.find((wD) => wD.id === w)?.name)
    .join(', ');

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Who did you reach?</p>
      <p className={style.text}>{whoDidYouReachNames}</p>
    </Stack>
  );
};

export const HowDidYouReachThem = ({ note }: DisplayProps) => {
  const howDidYouReachThemName = howDidYouReachThemData.find(
    (h) => h.id === note.howDidYouReachThem
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>How did you reach them?</p>
      <p className={style.text}>{howDidYouReachThemName}</p>
    </Stack>
  );
};

export const WhatTypeOfPlaceWereYouIn = ({ note }: DisplayProps) => {
  const whatTypeOfPlaceWereYouInName = whatTypeOfPlaceWereYouInData.find(
    (w) => w.id === note.whatTypeOfPlaceWereYouIn
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>What type of place were you in?</p>
      <p className={style.text}>{whatTypeOfPlaceWereYouInName}</p>
    </Stack>
  );
};

export const WhoWereYouTryingToReach = ({ note }: DisplayProps) => {
  const whoWereYouTryingToReachName = whoWereYouTryingToReachData.find(
    (w) => w.id === note.whoWereYouTryingToReach
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Who were you trying to reach?</p>
      <p className={style.text}>{whoWereYouTryingToReachName}</p>
    </Stack>
  );
};

export const HowDidYouTryThem = ({ note }: DisplayProps) => {
  const howDidYouTryThemName = howDidYouReachThemData.find(
    (h) => h.id === note.howDidYouTryThem
  )?.name;

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>How did you try them?</p>
      <p className={style.text}>{howDidYouTryThemName}</p>
    </Stack>
  );
};

type NoteViewContentProps = {
  note: NoteItem;
  sitesEnabled?: boolean;
};

export const NoteViewContent = ({ note, sitesEnabled }: NoteViewContentProps) => {
  const isManagerReview = note.noteType === 'MANAGER_REVIEW';

  if (isManagerReview) {
    return (
      <Stack display="flex" gap="22px">
        <Stack display="flex" flexDirection="row">
          <NoteType note={note} />
          <DateOfEncounter note={note} />
        </Stack>
        <Stack display="flex" flexDirection="row">
          <TimeSpent note={note} />
          <Manager note={note} />
        </Stack>
        <Stack display="flex" flexDirection="row">
          <Chw note={note} />
          <ManagerReviewProvider note={note} />
        </Stack>
        <div className={style.dottedLine} />
        <Stack display="flex" flexDirection="row">
          <Stack display="flex" gap="8px" flex="1">
            <p className={style.label}>Note:</p>
            <p className={style.text}>{note.note}</p>
          </Stack>
        </Stack>
        <DocumentationTime note={note} />
      </Stack>
    );
  }

  const showWhoWereYouTryingToReach =
    note.whoDidYouReach?.length > 0 && note.whoDidYouReach?.includes('NO_ONE');

  const showHowDidYouTryThem =
    note.whoWereYouTryingToReach?.length > 0 &&
    note.whoWereYouTryingToReach !== 'WORK_ON_MY_CLIENTS_BEHALF' &&
    note.howDidYouTryThem !== '';

  const showWhichProvider =
    note.whoDidYouReach?.length > 0 && note.whoDidYouReach?.includes('PROVIDER');

  const showOtherProviderName = showWhichProvider && note.billingProviderId.toString() === '-1';

  const showHowDidYouReachThem =
    note.whoDidYouReach?.length > 0 && !note.whoDidYouReach?.includes('NO_ONE');

  const showWhatTypeOfPlaceWereYouIn =
    (showHowDidYouTryThem && note.howDidYouTryThem?.includes('IN_PERSON')) ||
    (showHowDidYouReachThem && note.howDidYouReachThem?.includes('IN_PERSON'));

  const showSites =
    sitesEnabled &&
    showHowDidYouReachThem &&
    note.whatTypeOfPlaceWereYouIn !== '' &&
    note.whatTypeOfPlaceWereYouIn !== null &&
    ['EMERGENCY_DEPARTMENT', 'HOSPITAL', 'MEDICAL_OFFICE', 'OTHER_IN_THE_COMMUNITY'].includes(
      note.whatTypeOfPlaceWereYouIn
    );

  const showZCodes = note.typeOfSupport?.length > 0 && !note.typeOfSupport?.includes('NONE');

  return (
    <Stack display="flex" gap="20px">
      <Stack display="flex" flexDirection="row">
        <NoteType note={note} />
        <DateOfEncounter note={note} />
      </Stack>
      <Stack display="flex" flexDirection="row">
        <StartTime note={note} />
        <TimeSpent note={note} />
      </Stack>
      <Stack display="flex" flexDirection="row">
        <NextContact note={note} />
      </Stack>
      <div className={style.dottedLine} />
      <Stack>
        <Grid container rowSpacing={3}>
          <Grid item xs={6}>
            <WhoDidYouReach note={note} />
          </Grid>
          {/* Reached NO_ONE */}
          {showWhoWereYouTryingToReach && (
            <Grid item xs={6}>
              <WhoWereYouTryingToReach note={note} />
            </Grid>
          )}
          {showHowDidYouTryThem && (
            <Grid item xs={6}>
              <HowDidYouTryThem note={note} />
            </Grid>
          )}
          {/* Reached Someone Else */}
          {showWhichProvider && (
            <Grid item xs={6}>
              <WhichProvider note={note} />
            </Grid>
          )}
          {showOtherProviderName && (
            <Grid item xs={6}>
              <OtherProviderName note={note} />
            </Grid>
          )}
          {showHowDidYouReachThem && (
            <Grid item xs={6}>
              <HowDidYouReachThem note={note} />
            </Grid>
          )}
          {showWhatTypeOfPlaceWereYouIn && (
            <Grid item xs={6}>
              <WhatTypeOfPlaceWereYouIn note={note} />
            </Grid>
          )}
          {showSites && (
            <Grid item xs={6}>
              <Sites note={note} />
            </Grid>
          )}
        </Grid>
      </Stack>
      <div className={style.dottedLine} />
      <Stack display="flex" flexDirection="row">
        <TypeOfSupport note={note} />
      </Stack>
      {showZCodes && (
        <Stack display="flex" flexDirection="row">
          <ZCodes note={note} />
        </Stack>
      )}
      <div className={style.dottedLine} />
      <Stack display="flex" flexDirection="row">
        <Stack display="flex" gap="8px" flex="1">
          <p className={style.label}>Note:</p>
          <p className={style.text}>{note.note}</p>
        </Stack>
      </Stack>
      <DocumentationTime note={note} />
    </Stack>
  );
};
